import { useCallback, useEffect, useState } from 'react';
import { supabase } from '../lib/supabase';
import { useAuth } from '../contexts/AuthContext';

export type SubscriptionStatus =
  | 'trialing'
  | 'active'
  | 'past_due'
  | 'canceled'
  | 'unpaid'
  | 'paused'
  | 'incomplete'
  | 'incomplete_expired'
  | null;

export interface SubscriptionInfo {
  status: SubscriptionStatus;
  periodEnd: Date | null;
  priceId: string | null;
  isActive: boolean; // true when user has full access
  cancelAtPeriodEnd: boolean;
  loading: boolean;
  error: string | null;
  startCheckout: (priceId: string) => Promise<void>;
  openBillingPortal: () => Promise<void>;
  cancelSubscription: () => Promise<{ cancelAt: string } | { error: string }>;
  refresh: () => Promise<void>;
}

const ACTIVE_STATUSES: SubscriptionStatus[] = ['active', 'trialing'];

export function useSubscription(): SubscriptionInfo {
  const { user } = useAuth();
  const [status, setStatus] = useState<SubscriptionStatus>(null);
  const [periodEnd, setPeriodEnd] = useState<Date | null>(null);
  const [priceId, setPriceId] = useState<string | null>(null);
  const [cancelAtPeriodEnd, setCancelAtPeriodEnd] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchSubscription = useCallback(async () => {
    if (!user) {
      setStatus(null);
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);

    const { data, error: fetchError } = await supabase
      .from('users')
      .select('subscription_status, subscription_period_end, stripe_price_id, subscription_cancel_at_period_end')
      .eq('id', user.id)
      .single();

    if (fetchError) {
      setError(fetchError.message);
    } else {
      setStatus((data?.subscription_status as SubscriptionStatus) ?? null);
      setPeriodEnd(data?.subscription_period_end ? new Date(data.subscription_period_end) : null);
      setPriceId(data?.stripe_price_id ?? null);
      setCancelAtPeriodEnd(data?.subscription_cancel_at_period_end ?? false);
    }
    setLoading(false);
  }, [user]);

  useEffect(() => {
    fetchSubscription();
  }, [fetchSubscription]);

  const edgeFunctionHeaders = async () => {
    const { data: { session } } = await supabase.auth.getSession();
    return {
      'Content-Type': 'application/json',
      'Authorization': session ? `Bearer ${session.access_token}` : '',
      'apikey': process.env.REACT_APP_SUPABASE_ANON_KEY ?? '',
    };
  };

  const parseEdgeFunctionResponse = async (res: Response) => {
    const text = await res.text();
    const json = text ? JSON.parse(text) : null;

    if (!res.ok) {
      throw new Error(
        json && typeof json === 'object' && 'error' in json && typeof json.error === 'string'
          ? json.error
          : `Request failed with status ${res.status}`,
      );
    }

    return json;
  };

  const startCheckout = useCallback(async (selectedPriceId: string) => {
    setError(null);
    try {
      const headers = await edgeFunctionHeaders();
      const res = await fetch(
        `${process.env.REACT_APP_SUPABASE_URL}/functions/v1/create-checkout-session`,
        {
          method: 'POST',
          headers,
          body: JSON.stringify({
            priceId: selectedPriceId,
            successUrl: `${window.location.origin}/contractor`,
            cancelUrl: `${window.location.origin}/contractor`,
          }),
        },
      );
      const json = await parseEdgeFunctionResponse(res);
      if (!json || typeof json !== 'object' || !('url' in json) || typeof json.url !== 'string') {
        throw new Error('Checkout session did not return a redirect URL');
      }
      window.location.href = json.url;
    } catch (checkoutError) {
      setError(checkoutError instanceof Error ? checkoutError.message : 'Failed to start checkout');
    }
  }, []);

  const openBillingPortal = useCallback(async () => {
    setError(null);
    try {
      const headers = await edgeFunctionHeaders();
      const res = await fetch(
        `${process.env.REACT_APP_SUPABASE_URL}/functions/v1/create-checkout-session`,
        {
          method: 'POST',
          headers,
          body: JSON.stringify({
            successUrl: `${window.location.origin}/contractor/profile`,
            cancelUrl: `${window.location.origin}/contractor/profile`,
          }),
        },
      );
      const json = await parseEdgeFunctionResponse(res);
      if (!json || typeof json !== 'object' || !('url' in json) || typeof json.url !== 'string') {
        throw new Error('Billing portal did not return a redirect URL');
      }
      window.location.href = json.url;
    } catch (billingPortalError) {
      setError(billingPortalError instanceof Error ? billingPortalError.message : 'Failed to open billing portal');
    }
  }, []);

  const cancelSubscription = useCallback(async () => {
    setError(null);
    try {
      const headers = await edgeFunctionHeaders();
      const res = await fetch(
        `${process.env.REACT_APP_SUPABASE_URL}/functions/v1/cancel-subscription`,
        {
          method: 'POST',
          headers,
        },
      );
      const json = await parseEdgeFunctionResponse(res);
      await fetchSubscription();
      return {
        cancelAt:
          json && typeof json === 'object' && 'cancelAt' in json ? json.cancelAt : undefined,
      };
    } catch (cancelError) {
      const message = cancelError instanceof Error ? cancelError.message : 'Failed to cancel';
      setError(message);
      return { error: message };
    }
  }, [fetchSubscription]);

  return {
    status,
    periodEnd,
    priceId,
    isActive: ACTIVE_STATUSES.includes(status),
    cancelAtPeriodEnd,
    loading,
    error,
    startCheckout,
    openBillingPortal,
    cancelSubscription,
    refresh: fetchSubscription,
  };
}
